/**
 * Banner displayed in flight/room guides when pricing data may be outdated.
 * Reads directly from the Firebolt model so signal reactivity works automatically.
 *
 * Model-driven states take priority (they reflect the latest Firestore data).
 * The hook's lastResult is only used for the transient "accurate" state (price confirmed unchanged).
 */

import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';

import type { RefreshPricingResult } from '#lib/useRefreshPricing.js';

import { useState } from 'react';

import { formatDate, formatTimeSince, toDate } from '@elara/client-core/lib/date-utils';
import { formatPrice } from '@elara/client-core/lib/utils';

import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';
import { IconSizes, MaterialIcon } from '#ui';

import { Alert, AlertDescription } from './alert.js';
import { Button } from './button.js';

type PriceStatusBannerProps = {
  /** Divide the price delta by this number to show per-unit changes (e.g. per passenger). Defaults to 1. */
  priceDivisor?: number;
  /** Label for the unit when priceDivisor > 1 (e.g. "per passenger", "per night"). */
  priceUnit?: string;
  /** Alert size — `sm` for compact contexts like the itinerary builder. Defaults to `default`. */
  size?: 'default' | 'sm';
  /** Extra classes applied to the rendered Alert. Skipped entirely when the banner renders nothing. */
  className?: string;
} & ({ type: 'flight'; model: FlightOptionModel } | { type: 'room'; model: RoomOptionModel });

export function PriceStatusBanner(props: PriceStatusBannerProps) {
  const { type, model, priceDivisor = 1, priceUnit, size, className } = props;

  const label = type === 'flight' ? props.model.displayTitle('all') : (props.model as RoomOptionModel).displayName;
  const canRefresh = useCanRefreshPricing(type, model);

  const { isRefreshing, lastResult, refresh } = useRefreshPricing({
    type,
    referenceId: model.id,
    enabled: canRefresh,
    showToasts: true,
    label,
  });

  // The "Price is accurate" confirmation is transient and dismissible. We key the
  // dismissal to the specific result object so a fresh recheck shows it again.
  const [dismissedResult, setDismissedResult] = useState<RefreshPricingResult | null>(null);

  // Read reactive properties from the model
  const fetchedAt = toDate(model.fetchedAt);
  const timeAgo = fetchedAt ? formatTimeSince(fetchedAt) : 'a while ago';
  const isStale = model.isStale;
  const isAvailable = model.isAvailable;

  // For rooms, the banner only shows staleness and partial availability -- not price deltas.
  const priceDelta = type === 'flight' ? model.priceDelta : null;
  const isPartiallyAvailable = type === 'room' ? (props.model as RoomOptionModel).isPartiallyAvailable : false;

  const priceHistory = type === 'flight' ? model.priceHistory : undefined;
  const originalEntry = priceHistory?.at(-1);
  const originalDate = originalEntry ? toDate((originalEntry as Record<string, unknown>).fetchedAt) : null;
  const originalDateStr = originalDate ? formatDate(originalDate, 'MMM d, yyyy') : null;
  const since = originalDateStr ? `since ${originalDateStr}` : 'since original search';
  const unitSuffix = priceDivisor > 1 && priceUnit ? ` ${priceUnit}` : '';
  const currency = model.cost?.currency;

  // Loading
  if (isRefreshing) {
    return (
      <Alert variant="warning" size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MaterialIcon icon="refresh" className="animate-spin" size={IconSizes.xs} />
            <span>Checking pricing and availability...</span>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Model-driven: unavailable (Firestore updated reactively after recheck)
  if (!isAvailable) {
    return (
      <Alert variant="destructive" size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MaterialIcon icon="error" size={IconSizes.xs} />
            <span>This {type} is no longer available. Ask Elara to help find a replacement.</span>
          </div>
          {canRefresh && (
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={refresh}>
              <MaterialIcon icon="refresh" />
              Recheck
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Model-driven: partially available
  if (isPartiallyAvailable) {
    return (
      <Alert variant="warning" size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MaterialIcon icon="error" size={IconSizes.xs} />
            <span>Some rates are no longer available</span>
          </div>
          <span>{`Checked ${timeAgo}`}</span>
        </AlertDescription>
      </Alert>
    );
  }

  // Model-driven: price changed (flights only in banner)
  if (priceDelta && priceDelta.direction !== 'same') {
    const icon = priceDelta.direction === 'up' ? 'trending_up' : 'trending_down';
    const variant = priceDelta.direction === 'up' ? 'warning' : 'success';
    const message =
      priceDelta.amount != null && currency
        ? `Price has ${priceDelta.direction === 'up' ? 'increased' : 'decreased'} by ${formatPrice(priceDelta.amount / priceDivisor, currency)}${unitSuffix} ${since}`
        : 'Price has been updated';

    return (
      <Alert variant={variant} size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MaterialIcon icon={icon} size={IconSizes.xs} />
            <span>{message}</span>
          </div>
          <span>{`Checked ${timeAgo}`}</span>
        </AlertDescription>
      </Alert>
    );
  }

  // Transient: recheck completed with no changes (model looks the same, so show confirmation)
  if (
    lastResult &&
    lastResult.available &&
    !lastResult.priceChanged &&
    !lastResult.partiallyAvailable &&
    lastResult !== dismissedResult
  ) {
    return (
      <Alert variant="success" size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MaterialIcon icon="check_circle" size={IconSizes.xs} />
            <span>Price is accurate</span>
          </div>
          <div className="flex items-center gap-2">
            <span>{`Checked ${timeAgo}`}</span>
            <Button
              variant="icon"
              size="icon-xs"
              className="flex-shrink-0"
              onClick={() => setDismissedResult(lastResult)}
              aria-label="Dismiss"
            >
              <MaterialIcon icon="close" size={IconSizes.xs} />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Stale: prompt for recheck (only if refresh is allowed)
  if (isStale && canRefresh) {
    return (
      <Alert variant="warning" size={size} className={className}>
        <AlertDescription className="flex items-center justify-between gap-2">
          <div>
            <div>Pricing and availability may be outdated.</div>
            <div className="text-xs text-muted-foreground">Last fetched {timeAgo}.</div>
          </div>
          <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={refresh}>
            <MaterialIcon icon="refresh" />
            Refresh
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
